import { useCallback } from 'react';
import { RateDraftReview } from '../components/RateDraftReview.js';
import { Badge, Metric, type Notify, Panel, btn, btnGhost } from '../components/ui.js';
import {
  type AdminSignature,
  getAiUsageSummary,
  listSignatures,
  rejectSignature,
  verifySignature,
} from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';

const usd = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const statusTone = (s: string) =>
  s === 'verified' ? 'emerald' : s === 'rejected' ? 'rose' : 'amber';

/** Back-office AI ops: pending rate drafts, AI usage/cost, and the statement-
 * signature library (with manual verify/reject). */
export function AiOps({ notify }: { notify: Notify }) {
  const sigs = useAsyncData(useCallback(() => listSignatures().then((r) => r.signatures), []));
  const usage = useAsyncData(useCallback(() => getAiUsageSummary(), []));
  const signatures: AdminSignature[] = sigs.data ?? [];
  const u = usage.data;

  const act = async (p: Promise<unknown>, ok: string) => {
    try {
      await p;
      notify(ok, 'success');
      sigs.reload();
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Action failed', 'error');
    }
  };

  return (
    <div className="space-y-5">
      <RateDraftReview notify={notify} />

      {u && (
        <Panel title={`AI usage — ${u.period}`}>
          <div className="grid grid-cols-3 gap-3">
            <Metric label="Queries" value={u.totals.queries.toLocaleString()} />
            <Metric label="Tokens" value={u.totals.tokens.toLocaleString()} />
            <Metric label="Cost" value={usd(u.totals.costCents)} tone="emerald" />
          </div>
          {u.byAccount.length > 0 && (
            <table className="mt-3 w-full text-sm">
              <thead className="text-left text-neutral-500">
                <tr>
                  <th className="py-1">Account</th>
                  <th>Queries</th>
                  <th>Tokens</th>
                  <th className="text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {u.byAccount.map((a) => (
                  <tr key={a.accountId} className="border-t border-neutral-100">
                    <td className="py-1">{a.accountName}</td>
                    <td>{a.queries.toLocaleString()}</td>
                    <td>{a.tokens.toLocaleString()}</td>
                    <td className="text-right">{usd(a.costCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      <Panel title={`Statement-signature library (${signatures.length})`}>
        <p className="mb-3 text-xs text-neutral-500">
          Every learned statement format. A <b>candidate</b> mapping auto-promotes to{' '}
          <b>verified</b> after enough distinct accounts ingest it cleanly — or verify/reject it
          here. Rejecting stops reuse (the next upload of that format rebuilds it).
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-neutral-500">
              <tr>
                <th className="py-1 pr-3">Carrier</th>
                <th className="pr-3">Status</th>
                <th className="pr-3">Accounts</th>
                <th className="pr-3">Clean</th>
                <th className="pr-3">Coverage</th>
                <th className="pr-3">Built by</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {signatures.map((s) => (
                <tr key={s.id} className="border-t border-neutral-100 align-top">
                  <td className="py-1.5 pr-3">
                    {s.carrierName} <span className="text-xs text-neutral-400">· {s.fileType}</span>
                  </td>
                  <td className="pr-3">
                    <Badge tone={statusTone(s.status)}>{s.status}</Badge>
                  </td>
                  <td className="pr-3">{s.distinctAccounts}</td>
                  <td className="pr-3">{s.cleanIngestCount}</td>
                  <td className="pr-3">
                    {s.confidence != null ? `${Math.round(s.confidence * 100)}%` : '—'}
                  </td>
                  <td className="pr-3 text-xs text-neutral-500">
                    {s.builtBy}
                    {s.model ? ` · ${s.model.split('/').pop()}` : ''}
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      {s.status !== 'verified' && (
                        <button
                          type="button"
                          className={btn}
                          onClick={() => act(verifySignature(s.id), 'Signature verified')}
                        >
                          Verify
                        </button>
                      )}
                      {s.status !== 'rejected' && (
                        <button
                          type="button"
                          className={btnGhost}
                          onClick={() => act(rejectSignature(s.id), 'Signature rejected')}
                        >
                          Reject
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {signatures.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-3 text-center text-neutral-400">
                    No signatures yet — they appear as agencies upload statements.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
